import { math } from "../../math";
import { ProcessedTextItem, proccessTextItems } from "./processed-text-item";
import { RenderingContext2D } from "./rendering-context-2d";

export class CanvasRenderingContext2DExt extends RenderingContext2D{
	public constructor(
		private readonly ctx:CanvasRenderingContext2D,
	){
		super();
	}

	protected _pxWidth?:number;
	protected _pxUp?:math.Vec2;
	protected static readonly gradiantObjectForwardMap=new WeakMap<RenderingContext2D.Gradient,CanvasGradient>();
	protected static readonly gradiantObjectReverseMap=new WeakMap<CanvasGradient,RenderingContext2D.Gradient>();

	public get width(){
		return this.ctx.canvas.width;
	}

	public get height(){
		return this.ctx.canvas.height;
	}

	public save(){
		this.ctx.save();
	}

	public restore(){
		this._pxWidth=undefined;
		this._pxUp=undefined;
		this.ctx.restore();
	}

	public getTransform(){
		return this.ctx.getTransform();
	}

	public setTransform(m:DOMMatrix){
		return this.ctx.setTransform(m);
	}

	public resetTransform(){
		return this.ctx.resetTransform();
	}

	public transform(a:number, b:number, c:number, d:number, e:number, f:number):void{
		this._pxWidth=undefined;
		this._pxUp=undefined;
		this.ctx.transform(a,b,c,d,e,f);
	}

	public translate(x:number, y:number){
		this._pxWidth=undefined;
		this._pxUp=undefined;
		this.ctx.translate(x,y);
	}

	public scale(x:number, y:number){
		this._pxWidth=undefined;
		this._pxUp=undefined;
		this.ctx.scale(x,y);
	}

	public rotate(angle:number){
		this._pxWidth=undefined;
		this._pxUp=undefined;
		this.ctx.rotate(angle);
	}

	private calcPxValues(){
		if(this._pxWidth===undefined){
			let m=this.ctx.getTransform().inverse();
			this._pxUp=new math.Vec2(m.c,m.d).negate();
			this._pxWidth=this._pxUp.len();
			this._pxUp.invScale(this._pxWidth);
			this._pxWidth*=this.pxUnitFactor;
		}
	}

	public pxWidth(v=1){
		this.calcPxValues();
		return v*this._pxWidth!;
	}

	public pxUp(){
		this.calcPxValues();
		return this._pxUp!.clone();
	}

	public get globalAlpha(){
		return this.ctx.globalAlpha;
	}
	public set globalAlpha(v){
		this.ctx.globalAlpha=v;
	}

	public get fillStyle(){
		return this.ctx.fillStyle;
	}
	public set fillStyle(v){
		this.ctx.fillStyle=v;
	}

	public setLineDash(segments:number[]){
		this.ctx.setLineDash(segments);
	}

	public get lineDashOffset(){
		return this.ctx.lineDashOffset;
	}
	public set lineDashOffset(v){
		this.ctx.lineDashOffset=v;
	}

	public get lineWidth(){
		return this.ctx.lineWidth;
	}
	public set lineWidth(v){
		this.ctx.lineWidth=v;
	}

	public get lineCap(){
		return this.ctx.lineCap;
	}
	public set lineCap(v){
		this.ctx.lineCap=v;
	}

	public get lineJoin(){
		return this.ctx.lineJoin;
	}
	public set lineJoin(v){
		this.ctx.lineJoin=v;
	}

	public get strokeStyle():string|RenderingContext2D.Gradient{
		let v=this.ctx.strokeStyle;
		if(v instanceof CanvasGradient)
			CanvasRenderingContext2DExt.gradiantObjectReverseMap.get(v)!;
		if(typeof(v)!=='string')
			v='black';
		return v;
	}
	public set strokeStyle(v){
		if(v instanceof RenderingContext2D.Gradient){
			let gradient=CanvasRenderingContext2DExt.gradiantObjectForwardMap.get(v);
			if(!gradient){
				gradient=this.ctx.createLinearGradient(v.a.x,v.a.y,v.b.x,v.b.y);
				for(const [p,color] of v.stops)
					gradient.addColorStop(p,color.toString());
				CanvasRenderingContext2DExt.gradiantObjectForwardMap.set(v,gradient);
			}
			this.ctx.strokeStyle=gradient;
		}else{
			this.ctx.strokeStyle=v;
		}
	}

	public get shadowBlur(){
		return this.ctx.shadowBlur;
	}
	public set shadowBlur(v){
		this.ctx.shadowBlur=v;
	}

	public get shadowColor(){
		return this.ctx.shadowColor;
	}
	public set shadowColor(v){
		this.ctx.shadowColor=v;
	}

	public get shadowOffsetX(){
		return this.ctx.shadowOffsetX;
	}
	public set shadowOffsetX(v){
		this.ctx.shadowOffsetX=v;
	}

	public get shadowOffsetY(){
		return this.ctx.shadowOffsetY;
	}
	public set shadowOffsetY(v){
		this.ctx.shadowOffsetY=v;
	}

	public beginPath(){
		this.ctx.beginPath();
	}

	public moveTo(x:number, y:number){
		this.ctx.moveTo(x,y);
	}

	public lineTo(x:number, y:number){
		this.ctx.lineTo(x,y);
	}

	public closePath(){
		this.ctx.closePath();
	}

	public rect(x:number, y:number, w:number, h:number){
		this.ctx.rect(x,y,w,h);
	}

	public circle(x:number, y:number, radius:number){
		this.ctx.ellipse(x,y,radius,radius,0,0,Math.PI*2);
	}

	public ellipse(x:number, y:number, radiusX:number, radiusY:number, rotation:number, startAngle:number, endAngle:number, counterclockwise?:boolean){
		this.ctx.ellipse(x,y,radiusX,radiusY,rotation,startAngle,endAngle,counterclockwise);
	}

	public arc(x:number, y:number, radius:number, startAngle:number, endAngle:number, counterclockwise?:boolean){
		this.ctx.arc(x,y,radius,startAngle,endAngle,counterclockwise);
	}

	public stroke(path?:Path2D){
		if(path)
			this.ctx.stroke(path);
		else
			this.ctx.stroke();
	}

	public fill():void;
	public fill(path:Path2D):void;
	public fill(path?:Path2D){
		if(path)
			this.ctx.fill(path);
		else
			this.ctx.fill();
	}

	public clip():void;
	public clip(fillRule:CanvasFillRule):void;
	public clip(fillRule?:CanvasFillRule){
		if(fillRule)
			this.ctx.clip(fillRule);
		else
			this.ctx.clip();
	}

	public get font(){
		return this.ctx.font;
	}
	public set font(v){
		this.ctx.font=v;
	}

	public get textAlign(){
		return this.ctx.textAlign;
	}
	public set textAlign(v){
		this.ctx.textAlign=v;
	}

	public get textBaseline(){
		return this.ctx.textBaseline;
	}
	public set textBaseline(v){
		this.ctx.textBaseline=v;
	}

	public measureText(text:string){
		return this.ctx.measureText(text);
	}

	public strokeText(text:string, x:number, y:number, maxWidth?:number){
		return this.ctx.strokeText(text,x,y,maxWidth);
	}

	public fillText(text:string, x:number, y:number, maxWidth?:number){
		return this.ctx.fillText(text,x,y,maxWidth);
	}

	public clearRect(x:number, y:number, w:number, h:number){
		this.ctx.clearRect(x,y,w,h);
	}

	public fillRect(x:number, y:number, w:number, h:number){
		this.ctx.fillRect(x,y,w,h);
	}

	public strokeRect(x:number, y:number, w:number, h:number){
		this.ctx.strokeRect(x,y,w,h);
	}

    public drawImage(image:CanvasImageSource, dx:number, dy:number):void;
    public drawImage(image:CanvasImageSource, dx:number, dy:number, dw:number, dh:number):void;
    public drawImage(image:CanvasImageSource, sx:number, sy:number, sw:number, sh:number, dx:number, dy:number, dw:number, dh:number):void;
	public drawImage(...args:any[]){
		this.ctx.drawImage.apply(this.ctx,<any>args);
	}

	public processText(
		items:RenderingContext2D.TextItem[],
		style:RenderingContext2D.TextStyle={},
	){
		const {ctx}=this;
		const pxWidth=this.pxWidth();
		ctx.save();
		ctx.resetTransform();
		const out=proccessTextItems(
			ctx,
			pxWidth,
			this.pxUnitFactor,
			this.defaultTextStyle,
			this.textTransformer,
			items,
			style,
		);
		ctx.restore();
		return out;
	}

	public drawProcessedText(
		items:ProcessedTextItem[],
		callback?:(transform:DOMMatrix, item:ProcessedTextItem, index:number)=>void,
	):void{
		if(!items.length)
			return;
		const {ctx}=this;
		ctx.save();
		const coordToPixel=ctx.getTransform();
		ctx.resetTransform();
		ctx.textBaseline='hanging';
		for(const [index,item] of items.entries()){
			const [pos,off,rot]=item.pixelOrientation(coordToPixel,this.viewDownVecPx);
			ctx.save();
			ctx.translate(pos.x,pos.y);
			// ctx.fillStyle='red';
			// ctx.beginPath();
			// this.circle(0,0,4);
			// ctx.fill();
			ctx.rotate(rot);
			ctx.translate(off.x,off.y);
			if(callback)
				callback(ctx.getTransform(),item,index);
			item.draw(ctx);
			ctx.restore();
		}
		ctx.restore();
	}
}

export namespace CanvasRenderingContext2DExt{
	export function get(
		canvas:HTMLCanvasElement,
	){
		const ctx=canvas?.getContext('2d');
		if(ctx)
			return new CanvasRenderingContext2DExt(ctx);
		return null;
	}
}
